import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { studentService, departmentService } from '@/services/api'
import { UserPlus, Upload, Download, Search, Edit, Trash2, Layers, MonitorUp } from 'lucide-react'
import StudentModal from '@/components/students/StudentModal'
import ImportModal from '@/components/common/ImportModal'
import BulkImportModal from '@/components/common/BulkImportModal'
import SyncToTerminalModal from '@/components/common/SyncToTerminalModal'
import Modal from '@/components/common/Modal'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const STATUS_BADGE = {
  active: 'badge-green',
  expelled: 'badge-red',
  academic_leave: 'badge-yellow',
  graduated: 'badge-blue',
}

const STATUS_LABEL = {
  active: 'Активен',
  expelled: 'Отчислен',
  academic_leave: 'Акад. отпуск',
  graduated: 'Выпускник',
}

export default function StudentsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({ 
    status: '', department: '', direction: '', group: '', has_photo: '', is_registered: '' 
  })
  const [page, setPage] = useState(1)
  const [modal, setModal] = useState({ open: false, data: null })
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [bulkImportOpen, setBulkImportOpen] = useState(false)
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' })
  const [deleting, setDeleting] = useState(false)

  const [selectedIds, setSelectedIds] = useState([])
  const [allSelectedGlobal, setAllSelectedGlobal] = useState(false)
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false)
  const [syncTerminalOpen, setSyncTerminalOpen] = useState(false)

  const params = {
    search,
    page,
    status: filters.status || undefined,
    department: filters.department || undefined,
    direction: filters.direction || undefined,
    group: filters.group || undefined,
    has_photo: filters.has_photo !== '' ? filters.has_photo : undefined,
    is_registered_on_device: filters.is_registered !== '' ? filters.is_registered : undefined,
  }

  const { data, isLoading } = useQuery({
    queryKey: ['students', params],
    queryFn: () => studentService.list(params).then((r) => r.data),
  })

  // Helper selectors
  const pageIds = (data?.results || []).map(s => s.id)
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id))

  const { data: hierarchy } = useQuery({
    queryKey: ['departments-hierarchy'],
    queryFn: () => departmentService.hierarchy().then((r) => r.data),
  })

  // Derived filter options
  const selectedDept = (hierarchy || []).find(d => d.id === Number(filters.department))
  const directionsList = selectedDept?.directions || []
  const selectedDir = directionsList.find(d => d.id === Number(filters.direction))
  const groupsList = selectedDir?.groups || []

  const toggleSelect = (id) => {
    setAllSelectedGlobal(false)
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)))
      setAllSelectedGlobal(false)
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...pageIds])])
    }
  }

  const handleBulkDelete = async () => {
    setDeleting(true)
    try {
      const payload = allSelectedGlobal ? { all_selected: true } : { ids: selectedIds }
      const { page: _, ...filterParams } = params

      await studentService.bulkDelete(payload, filterParams)
      toast.success(allSelectedGlobal ? `Все записи (${data.count}) удалены` : `Удалено ${selectedIds.length} записей`)
      qc.invalidateQueries(['students'])
      setSelectedIds([])
      setAllSelectedGlobal(false)
      setBulkDeleteModal(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка удаления')
    } finally {
      setDeleting(false)
    }
  }

  const handleImport = async (file) => {
    const resp = await studentService.importExcel(file)
    qc.invalidateQueries(['students'])
    return resp
  }

  const handleBulkImport = async (excel, zip) => {
    const resp = await studentService.bulkImport(excel, zip)
    qc.invalidateQueries(['students'])
    return resp
  }

  const handleExport = async () => {
    try {
      const resp = await studentService.exportExcel(params)
      const url = URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'students.xlsx'
      a.click()
    } catch {
      toast.error('Ошибка экспорта')
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await studentService.delete(deleteModal.id)
      toast.success('Ученик удалён')
      qc.invalidateQueries(['students'])
      setDeleteModal({ open: false, id: null, name: '' })
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-4 md:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Ученики</h1>
          {!isLoading && data && (
            <p className="text-sm text-gray-400 mt-0.5">{data.count} записей</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedIds.length > 0 ? (
            <>
              <button className="btn-secondary" onClick={() => setSyncTerminalOpen(true)}>
                <MonitorUp size={16} /> <span className="hidden sm:inline">На терминал</span> ({allSelectedGlobal ? data?.count : selectedIds.length})
              </button>
              <button className="btn-danger" onClick={() => setBulkDeleteModal(true)}>
                <Trash2 size={16} /> <span className="hidden sm:inline">Удалить</span> ({allSelectedGlobal ? data?.count : selectedIds.length})
              </button>
            </>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => setSyncTerminalOpen(true)}>
                <MonitorUp size={16} /> <span className="hidden sm:inline">На терминал</span>
              </button>
              <button className="btn-secondary hidden sm:flex" onClick={() => setBulkImportOpen(true)}>
                <Layers size={16} /> Массовый импорт
              </button>
              <button className="btn-secondary" onClick={() => setImportModalOpen(true)}>
                <Upload size={16} /> <span className="hidden sm:inline">Импорт Excel</span>
              </button>
              <button className="btn-secondary" onClick={handleExport}>
                <Download size={16} /> <span className="hidden sm:inline">Экспорт</span>
              </button>
              <button className="btn-primary" onClick={() => setModal({ open: true, data: null })}>
                <UserPlus size={16} /> <span className="hidden sm:inline">Добавить</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card !p-3">
        {/* Horizontal scrollable filters for mobile to save space */}
        <div className="flex md:flex-wrap overflow-x-auto pb-1 md:pb-0 gap-3 hide-scrollbar">
          <div className="flex-none w-64 md:flex-1 md:w-auto md:min-w-48 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 p-0.5" />
            <input
              className="input pl-9 w-full"
              placeholder="Поиск по ФИО, ID..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
          <select
            className="input w-auto flex-none"
            value={filters.status}
            onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1) }}
          >
            <option value="">Все статусы</option>
            <option value="active">Активен</option>
            <option value="expelled">Отчислен</option>
            <option value="academic_leave">Акад. отпуск</option>
            <option value="graduated">Выпускник</option>
          </select>
          <select
            className="input w-auto flex-none"
            value={filters.department}
            onChange={(e) => { setFilters((f) => ({ ...f, department: e.target.value, direction: '', group: '' })); setPage(1) }}
          >
            <option value="">Все кафедры/отделы</option>
            {(hierarchy || []).map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            className="input w-auto flex-none"
            value={filters.direction}
            onChange={(e) => { setFilters((f) => ({ ...f, direction: e.target.value, group: '' })); setPage(1) }}
            disabled={!filters.department || directionsList.length === 0}
          >
            <option value="">Все направления</option>
            {directionsList.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            className="input w-auto flex-none"
            value={filters.group}
            onChange={(e) => { setFilters((f) => ({ ...f, group: e.target.value })); setPage(1) }}
            disabled={!filters.direction || groupsList.length === 0}
          >
            <option value="">Все группы</option>
            {groupsList.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <select
            className="input w-auto flex-none"
            value={filters.has_photo}
            onChange={(e) => { setFilters((f) => ({ ...f, has_photo: e.target.value })); setPage(1) }}
          >
            <option value="">Все фото</option>
            <option value="true">С фото</option>
            <option value="false">Без фото</option>
          </select>
          <select
            className="input w-auto flex-none"
            value={filters.is_registered}
            onChange={(e) => { setFilters((f) => ({ ...f, is_registered: e.target.value })); setPage(1) }}
          >
            <option value="">Синхронизация</option>
            <option value="true">Синхр.</option>
            <option value="false">Не синхр.</option>
          </select>
        </div>
      </div>

      {selectedIds.length > 0 && data?.count > (data?.results || []).length && (
        <div className="bg-primary-50 border border-primary-100 rounded-lg py-2 px-4 flex items-center justify-center gap-2 text-sm text-primary-900">
          {allSelectedGlobal ? (
            <>
              Выбраны все ученики ({data.count}).{' '}
              <button
                className="text-primary-600 font-semibold hover:underline"
                onClick={() => {
                  setAllSelectedGlobal(false)
                  setSelectedIds([])
                }}
              >
                Очистить выбор
              </button>
            </>
          ) : (
            <>
              Выбрано <strong>{selectedIds.length}</strong> учеников на этой странице.{' '}
              <button
                className="text-primary-600 font-semibold hover:underline"
                onClick={() => setAllSelectedGlobal(true)}
              >
                Выбрать всех ({data.count})
              </button>
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-gray-100">
          {isLoading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="p-4 animate-pulse flex gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))
          ) : (data?.results || []).length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <UserPlus size={32} className="mx-auto mb-2 opacity-30" />
              <p>Учеников не найдено</p>
            </div>
          ) : (
            (data?.results || []).map((student) => (
              <div
                key={student.id}
                className={clsx(
                  'relative p-4 bg-white active:bg-gray-50 transition-colors',
                  selectedIds.includes(student.id) && 'bg-primary-50/50'
                )}
                onClick={() => navigate(`/students/${student.id}`)}
              >
                <div className="flex items-center gap-4">
                  {/* Select Checkbox (Optional) */}
                  <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                    <input type="checkbox" className="checkbox w-5 h-5 rounded-full" checked={selectedIds.includes(student.id)} onChange={() => toggleSelect(student.id)} />
                  </div>
                  
                  {/* Avatar */}
                  {student.photo ? (
                    <img src={student.photo} className="w-12 h-12 rounded-full object-cover shadow-sm shrink-0" alt="" />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200 rounded-full flex items-center justify-center text-lg text-gray-500 font-semibold shadow-sm shrink-0">
                      {student.full_name[0]}
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate text-[15px]">{student.full_name}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                      <span className="truncate">{student.class_name}</span>
                      <span className="w-1 h-1 bg-gray-300 rounded-full shrink-0"></span>
                      <span className="font-mono text-[11px] truncate">{student.student_id}</span>
                    </div>
                  </div>

                  {/* Status & Actions */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={clsx('text-[10px] px-2 py-0.5 rounded-full font-medium', STATUS_BADGE[student.status] || 'bg-gray-100 text-gray-600')}>
                      {STATUS_LABEL[student.status]}
                    </span>
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 active:bg-blue-50 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); setModal({ open: true, data: student }) }}><Edit size={16} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600 active:bg-red-50 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, id: student.id, name: student.full_name }) }}><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={allSelectedGlobal || allPageSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>#</th>
                <th>ФИО</th>
                <th>Номер ученика</th>
                <th>Группа</th>
                
                <th>Телефон</th>
                <th>Face ID</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array(6).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(9).fill(0).map((_, j) => (
                      <td key={j}>
                        <div className="h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : (data?.results || []).length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400">
                    <UserPlus size={32} className="mx-auto mb-2 opacity-30" />
                    <p>Учеников не найдено</p>
                  </td>
                </tr>
              ) : (
                (data?.results || []).map((student, i) => (
                  <tr
                    key={student.id}
                    className={clsx(
                      "cursor-pointer transition-colors",
                      selectedIds.includes(student.id) ? "bg-primary-50 hover:bg-primary-100" : "hover:bg-gray-50"
                    )}
                    onClick={() => navigate(`/students/${student.id}`)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedIds.includes(student.id)}
                        onChange={() => toggleSelect(student.id)}
                      />
                    </td>
                    <td className="text-gray-400 text-xs">{(page - 1) * 20 + i + 1}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        {student.photo ? (
                          <img src={student.photo} className="w-8 h-8 rounded-full object-cover" alt="" />
                        ) : (
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-500 font-bold">
                            {student.full_name[0]}
                          </div>
                        )}
                        <span className="font-medium">{student.full_name}</span>
                      </div>
                    </td>
                    <td className="font-mono text-xs">{student.student_id}</td>
                    <td>{student.class_name}</td>
                    
                    <td className="text-gray-500">{student.phone || '—'}</td>
                    <td>
                      {student.is_registered_on_device ? (
                        <span className="badge-green">Зарегистрирован</span>
                      ) : (
                        <span className="badge-gray">Нет</span>
                      )}
                    </td>
                    <td>
                      <span className={STATUS_BADGE[student.status] || 'badge-gray'}>
                        {STATUS_LABEL[student.status] || student.status}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <button
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          onClick={() => setModal({ open: true, data: student })}
                          title="Редактировать"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          onClick={() => setDeleteModal({ open: true, id: student.id, name: student.full_name })}
                          title="Удалить"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data?.count > 20 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Показано {(page - 1) * 20 + 1}–{Math.min(page * 20, data.count)} из {data.count}
            </p>
            <div className="flex gap-1">
              <button
                className="btn-secondary btn-sm"
                disabled={!data.previous}
                onClick={() => setPage((p) => p - 1)}
              >
                Назад
              </button>
              <button
                className="btn-secondary btn-sm"
                disabled={!data.next}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперед
              </button>
            </div>
          </div>
        )}
      </div>

      <StudentModal
        open={modal.open}
        onClose={() => setModal({ open: false, data: null })}
        student={modal.data}
      />

      <ImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        title="Импорт студентов"
        onImport={handleImport}
        downloadTemplateUrl="/media/templates/students_template.xlsx"
      />

      <BulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        title="Массовый импорт студентов"
        onImport={handleBulkImport}
        downloadTemplateUrl="/media/templates/students_template.xlsx"
      />

      <SyncToTerminalModal
        open={syncTerminalOpen}
        onClose={() => {
          setSyncTerminalOpen(false)
          setSelectedIds([])
          setAllSelectedGlobal(false)
        }}
        personType="student"
        personIds={allSelectedGlobal ? [] : selectedIds}
        allSelected={allSelectedGlobal}
      />

      {/* Bulk Delete Confirm */}
      <Modal
        open={bulkDeleteModal}
        onClose={() => !deleting && setBulkDeleteModal(false)}
        title="Массовое удаление"
        size="sm"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setBulkDeleteModal(false)}
              disabled={deleting}
            >
              Отмена
            </button>
            <button className="btn-danger" onClick={handleBulkDelete} disabled={deleting}>
              {deleting ? 'Удаление...' : 'Удалить выбранные'}
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Вы уверены, что хотите удалить <strong>{allSelectedGlobal ? data?.count : selectedIds.length}</strong> выбраных студентов?
        </p>
        <p className="text-sm text-red-500 mt-2 font-medium">
          {allSelectedGlobal ? 'ВНИМАНИЕ: Это удалит ВСЕ записи, подходящие под текущие фильтры!' : 'Это действие необратимо!'}
        </p>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={deleteModal.open}
        onClose={() => !deleting && setDeleteModal({ open: false, id: null, name: '' })}
        title="Удаление студента"
        size="sm"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setDeleteModal({ open: false, id: null, name: '' })}
              disabled={deleting}
            >
              Отмена
            </button>
            <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Удаление...' : 'Удалить'}
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Удалить студента <strong>«{deleteModal.name}»</strong>?
        </p>
        <p className="text-sm text-gray-400 mt-2">
          История посещаемости сохранится.
        </p>
      </Modal>
    </div>
  )
}
